import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Card, Icon, PageHeader, StatusView, type StatusTone } from '@/components/ui';
import type { IconName } from '@/components/ui';

import {
  checkBluetoothSupport,
  readNoticeAccepted,
  writeNoticeAccepted,
  type BluetoothSupport,
} from '../bluetooth';
import { mindosConfig, mindosEntryUrl } from '../config';
import styles from './MindOSPage.module.css';

const problems: Record<
  Exclude<BluetoothSupport['status'], 'ready'>,
  { icon: IconName; tone: StatusTone; title: string; message: string }
> = {
  'insecure-context': {
    icon: 'lock',
    tone: 'warning',
    title: 'Bluetooth needs a secure connection',
    message:
      'Browsers only allow Bluetooth on pages served over https:// (or localhost). Open the dashboard through its https address to connect your headband.',
  },
  ios: {
    icon: 'bluetooth',
    tone: 'warning',
    title: 'Bluetooth is not available on iPhone or iPad',
    message:
      'iOS browsers do not support Web Bluetooth. Open the dashboard in Google Chrome or Microsoft Edge on a Windows, Mac, Android or ChromeOS device.',
  },
  'unsupported-browser': {
    icon: 'bluetooth',
    tone: 'warning',
    title: 'This browser cannot connect to the headband',
    message:
      'MindOS uses Web Bluetooth, which is available in Google Chrome and Microsoft Edge. Open this page in Chrome to connect your ela headband.',
  },
  'adapter-off': {
    icon: 'bluetooth',
    tone: 'warning',
    title: 'Bluetooth appears to be turned off',
    message:
      'Turn on Bluetooth in your device settings, then check again. MindOS needs it to receive the EEG signal from your ela headband.',
  },
};

/**
 * Hosts the MindOS live-brain web app inside the dashboard.
 *
 * MindOS is embedded as-is from /modules/mindos/ in a same-origin iframe that
 * is explicitly allowed to use Bluetooth and full screen. The only thing this
 * page adds is a short notice that the browser will ask for Bluetooth access.
 */
export function MindOSPage() {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [support, setSupport] = useState<BluetoothSupport | null>(null);
  const [accepted, setAccepted] = useState(() => readNoticeAccepted(mindosConfig.noticeKey));
  const [bypass, setBypass] = useState(false);
  const src = mindosEntryUrl();

  const runCheck = useCallback(() => {
    setSupport(null);
    void checkBluetoothSupport().then(setSupport);
  }, []);

  useEffect(runCheck, [runCheck]);

  const accept = () => {
    writeNoticeAccepted(mindosConfig.noticeKey, true);
    setAccepted(true);
  };

  const showNotice = () => {
    writeNoticeAccepted(mindosConfig.noticeKey, false);
    setAccepted(false);
  };

  const goFullscreen = () => {
    void frameRef.current?.requestFullscreen?.().catch(() => undefined);
  };

  const problem = support && support.status !== 'ready' && !bypass ? problems[support.status] : null;
  const showFrame = support !== null && !problem && accepted;

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Live"
        title="MindOS"
        description="See the brain state in real time from your ela headband."
        actions={
          showFrame ? (
            <>
              <Button variant="ghost" size="sm" icon="info" onClick={showNotice}>
                Bluetooth help
              </Button>
              <Button variant="secondary" size="sm" icon="zoom-in" onClick={goFullscreen}>
                Full screen
              </Button>
              <a className={styles.linkButton} href={src} target="_blank" rel="noopener">
                <Icon name="external" size={16} />
                Open in new tab
              </a>
            </>
          ) : null
        }
      />

      {support === null ? (
        <Card>
          <StatusView icon="bluetooth" title="Checking Bluetooth…" compact />
        </Card>
      ) : problem ? (
        <Card>
          <StatusView
            icon={problem.icon}
            tone={problem.tone}
            title={problem.title}
            message={problem.message}
            action={
              <div className={styles.actions}>
                <Button variant="primary" icon="refresh" onClick={runCheck}>
                  Check again
                </Button>
                <Button variant="ghost" onClick={() => setBypass(true)}>
                  Open MindOS anyway
                </Button>
              </div>
            }
          />
        </Card>
      ) : !accepted ? (
        <Card>
          <StatusView
            icon="bluetooth"
            tone="accent"
            title="Allow Bluetooth access to use MindOS"
            message={
              <>
                MindOS connects directly to your ela headband over Bluetooth. Make sure Bluetooth is
                turned on, then press <strong>Connect headband</strong> in MindOS — Chrome will ask
                for Bluetooth access and list nearby devices. Choose your ela headband and click{' '}
                <strong>Pair</strong>.
              </>
            }
            action={
              <Button variant="primary" icon="arrow-right" onClick={accept}>
                Continue to MindOS
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <div className={styles.notice} role="note">
            <Icon name="bluetooth" size={16} />
            <span>
              When Chrome asks for Bluetooth access, select your ela headband and click{' '}
              <strong>Pair</strong>.
            </span>
          </div>
          <div className={styles.frameWrap}>
            <iframe
              ref={frameRef}
              className={styles.frame}
              src={src}
              title="MindOS live brain"
              allow="bluetooth; fullscreen"
              allowFullScreen
            />
          </div>
        </>
      )}
    </div>
  );
}
